import { useEffect, useState, type FormEvent } from 'react'

import { createApiKey, getApiKeys, revokeApiKey } from '@/lib/api/client/settings'
import type { ApiKey } from '@/lib/api/schemas/settings.schema'

const ONE_TIME_WARNING = 'Ключ показывается один раз. Сохраните его сейчас — потом его нельзя будет посмотреть.'

function formatDate(value: string | null): string {
  if (value === null) return 'Никогда'
  return new Date(value).toLocaleString('ru-RU', { timeZone: 'Europe/Moscow' })
}

function errorMessage(reason: unknown, fallback: string): string {
  return reason instanceof Error && reason.message.length > 0 ? reason.message : fallback
}

export function ApiKeySettings() {
  const [keys, setKeys] = useState<readonly ApiKey[]>([])
  const [name, setName] = useState('')
  const [onecSyncOnly, setOnecSyncOnly] = useState(false)
  const [secret, setSecret] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<'create' | string | null>(null)
  const [pendingRevoke, setPendingRevoke] = useState<string | null>(null)
  const [copyNotice, setCopyNotice] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    const controller = new AbortController()
    getApiKeys(controller.signal)
      .then((loaded) => { if (active) setKeys(loaded) })
      .catch((reason: unknown) => { if (active && !controller.signal.aborted) setError(errorMessage(reason, 'Не удалось загрузить API-ключи.')) })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false; controller.abort() }
  }, [])

  const closeSecret = () => {
    setSecret(null)
    setCopyNotice(false)
  }

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (busy !== null) return
    setBusy('create')
    setError(null)
    setCopyNotice(false)
    try {
      const created = await createApiKey(name, onecSyncOnly ? 'onec_sync' : 'owner')
      setKeys((current) => [...current, created.key])
      setName('')
      setOnecSyncOnly(false)
      setSecret(created.secret)
    } catch (reason: unknown) {
      setError(errorMessage(reason, 'Не удалось создать API-ключ.'))
    } finally {
      setBusy(null)
    }
  }

  const confirmRevoke = async (keyId: string) => {
    if (busy !== null) return
    setBusy(keyId)
    setError(null)
    try {
      const revoked = await revokeApiKey(keyId)
      setKeys((current) => current.map((key) => key.id === revoked.id ? revoked : key))
      setPendingRevoke(null)
    } catch (reason: unknown) {
      setError(errorMessage(reason, 'Не удалось отозвать API-ключ.'))
    } finally {
      setBusy(null)
    }
  }

  const copySecret = async () => {
    if (secret === null) return
    try {
      await navigator.clipboard.writeText(secret)
      setCopyNotice(true)
    } catch {
      setError('Не удалось скопировать ключ. Скопируйте его вручную.')
    }
  }

  return (
    <section className="mb-12">
      <div className="mb-5">
        <h2 className="text-[18px] font-semibold tracking-tight text-ink">API-ключи</h2>
        <p className="mt-0.5 text-[13px] text-ink-muted">Именованные ключи для автоматизации с полным доступом администратора; ключ синкера 1С ограничен синхронизацией.</p>
      </div>

      <div className="surface-card rounded-md p-4 sm:p-5">
        <form className="flex flex-col gap-3 sm:flex-row sm:items-end" onSubmit={submit}>
          <label className="grid flex-1 gap-1.5 text-[12px] font-medium text-ink-2">
            Название ключа
            <input
              className="input w-full"
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={64}
              autoComplete="off"
              placeholder="Например, агент склада"
              disabled={busy !== null}
            />
          </label>
          <button type="submit" className="btn btn-primary min-h-10" disabled={busy !== null || name.trim().length === 0}>
            {busy === 'create' ? 'Создаём…' : 'Создать ключ'}
          </button>
        </form>
        <label className="mt-3 flex items-start gap-2 text-[12px] text-ink-2">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={onecSyncOnly}
            onChange={(event) => setOnecSyncOnly(event.target.checked)}
            disabled={busy !== null}
          />
          <span>Только для синкера 1С на gold01 — ключ не даёт доступа ни к чему, кроме синхронизации с 1С.</span>
        </label>

        {loading && <p role="status" className="mt-4 text-[12px] text-ink-muted">Загружаем API-ключи…</p>}
        {error && <p role="alert" className="mt-4 rounded-sm border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700">{error}</p>}

        <div className="mt-5 border-t border-rule pt-4">
          <h3 className="text-[13px] font-semibold text-ink">Созданные ключи</h3>
          {keys.length === 0 && !loading ? <p className="mt-2 text-[12px] text-ink-muted">Ключей пока нет.</p> : null}
          <ul className="mt-3 grid gap-2" aria-label="Список API-ключей">
            {keys.map((key) => (
              <li key={key.id} className="rounded-sm border border-rule bg-bone/30 p-3 text-[12px]">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold text-ink">
                      {key.name}
                      {key.scope === 'onec_sync' && <span className="ml-2 rounded-full bg-bone px-2 py-0.5 text-[11px] font-medium text-ink-2">Синхронизация 1С</span>}
                    </div>
                    <div className="mt-1 text-ink-muted">Префикс: <code className="font-mono text-ink-2">{key.prefix}</code></div>
                  </div>
                  {key.revokedAt !== null ? (
                    <span className="rounded-full bg-red-50 px-2 py-1 text-[11px] font-semibold text-red-700">Отозван {formatDate(key.revokedAt)}</span>
                  ) : pendingRevoke === key.id ? (
                    <div className="flex flex-wrap items-center gap-2 rounded-sm border border-red-200 bg-red-50 p-2 text-red-800">
                      <span>Отозвать ключ?</span>
                      <button type="button" className="rounded-sm bg-red-700 px-2 py-1 font-semibold text-white disabled:opacity-50" onClick={() => { void confirmRevoke(key.id) }} disabled={busy !== null}>Отозвать</button>
                      <button type="button" className="rounded-sm border border-red-300 bg-paper px-2 py-1 font-semibold" onClick={() => setPendingRevoke(null)} disabled={busy !== null}>Не отзывать</button>
                    </div>
                  ) : (
                    <button type="button" className="rounded-sm border border-red-200 px-2.5 py-1.5 font-semibold text-red-700 hover:bg-red-50" onClick={() => setPendingRevoke(key.id)} disabled={busy !== null}>Отозвать</button>
                  )}
                </div>
                <div className="mt-2 grid gap-1 text-[11px] text-ink-muted sm:grid-cols-2">
                  <span>Создан: {formatDate(key.createdAt)}</span>
                  <span>Последнее использование: {formatDate(key.lastUsedAt)}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {secret !== null && (
        <div className="fixed inset-0 z-[90] grid place-items-center bg-onyx/40 p-4" role="dialog" aria-modal="true" aria-labelledby="api-key-secret-title">
          <div className="w-full max-w-xl rounded-md border border-rule bg-paper p-5 shadow-2xl">
            <h3 id="api-key-secret-title" className="text-[18px] font-semibold text-ink">API-ключ создан</h3>
            <p className="mt-2 text-[13px] leading-relaxed text-ink-2">{ONE_TIME_WARNING}</p>
            <code className="mt-4 block break-all rounded-sm border border-rule bg-onyx p-3 text-[13px] text-white">{secret}</code>
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <button type="button" className="btn btn-ghost" onClick={() => { void copySecret() }}>Скопировать</button>
              <button type="button" className="btn btn-primary" onClick={closeSecret}>Закрыть</button>
            </div>
            {copyNotice && <p role="status" className="mt-3 text-right text-[12px] text-st-delivered">Ключ скопирован.</p>}
          </div>
        </div>
      )}
    </section>
  )
}
